// Component: TabBar.jsx
// Purpose: Presentational floating glass tab bar — the phone-tier primary nav
//   chrome, generalized from shell/BottomTabBar.jsx. NO routing logic lives
//   here (that stays in the shell wrapper): it just paints tabs and reports
//   taps via onSelect(key, tab). Glass surface + geometry come from tokens
//   (--tabbar-height / --tabbar-blur / --radius-sheet). Styling in
//   ds-components.css (.ds-tabbar-*).
//
//   `icon` may be a node, or a fn (active) => node when the glyph changes with
//   the active state (e.g. filled vs. outlined).
//
// Usage:
//   <TabBar tabs={TABS} activeKey={key} onSelect={(k) => navigate(routeFor(k))} />

import React, { useId } from "react";
import { motion, LayoutGroup } from "framer-motion";
import { cx } from "./cx";
import { TAP } from "./motion";
import { haptics } from "./haptics";

export function TabBar({ tabs = [], activeKey, onSelect, className, ...props }) {
  // Scope the gliding pill's layoutId to THIS bar so multiple TabBars on one
  // page (e.g. the showcase) don't animate the pill between each other.
  const groupId = useId();
  return (
    <nav aria-label="Primary" className={cx("ds-tabbar", className)} {...props}>
      <LayoutGroup id={groupId}>
      {tabs.map((t) => {
        const active = t.key === activeKey;
        return (
          <motion.button
            key={t.key}
            type="button"
            aria-label={t.label}
            aria-current={active ? "page" : undefined}
            onClick={() => {
              if (!active) haptics.tap(); // light confirm on an actual tab change
              onSelect?.(t.key, t);
            }}
            className={cx("ds-tabbar-item", active && "active", t.center && "center")}
          >
            {/* Slot = positioning context. The active pill is ONE shared layout
                element (layoutId) so it GLIDES between tabs when the active tab
                changes, instead of hard-swapping off/on. The press wrapper scales
                on tap, kept separate from the pill so the two never fight. */}
            <span className="ds-tabbar-slot">
              {active && (
                <motion.span
                  className="ds-tabbar-pill"
                  layoutId="tabbar-active-pill"
                  style={{ borderRadius: 16 }}
                  transition={{ type: "spring", stiffness: 500, damping: 42, mass: 0.8 }}
                />
              )}
              <motion.span className="ds-tabbar-press" whileTap={TAP}>
                <span className="ds-tabbar-icon" aria-hidden="true">
                  {typeof t.icon === "function" ? t.icon(active) : t.icon}
                </span>
                <span className="ds-tabbar-label">{t.label}</span>
              </motion.span>
            </span>
          </motion.button>
        );
      })}
      </LayoutGroup>
    </nav>
  );
}

export default TabBar;
